import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { useApiGet } from '../../../utils/httpClientTs';
import { getMyPermissions } from '../../../endpoints/usersTs';
import { hasUserApplicationPermission } from '../../../utils/permissions';
import {
  completeFoundationStudentVerification,
  finalApproveFoundationApplication,
  FOUNDATION_STAGE_LABELS,
  FOUNDATION_WAITING_LABELS,
  getFoundationApplication,
  patchFoundationApplication,
  postFoundationReviewAction,
  putFoundationInstitution,
  resolveFoundationTrusteeHold,
  saveFoundationACH,
  searchFoundationInstitutions,
  toFormAnswerInputs,
  uploadFoundationEnrollmentDocument,
  type FoundationApplicationDetail,
  type FoundationFormAnswerInput,
  type FoundationInstitutionSearchResult,
} from '../../../endpoints/foundation';
import PageHeader from '../../../Components/PageHeader';
import RecommendationEditor, {
  type RecommendationEditorMode,
} from './RecommendationEditor';
import FoundationReviewPanel from './FoundationReviewPanel';

function staffEditableStage(stage: string): boolean {
  return (
    stage === 'gate_trustee_hold' ||
    stage === 'parallel_review' ||
    stage === 'post_award_docs' ||
    stage === 'final_io_approval'
  );
}

export default function FoundationApplicantDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [institutionQuery, setInstitutionQuery] = useState('');
  const [installmentCount, setInstallmentCount] = useState('1');
  const [date1, setDate1] = useState('');
  const [date2, setDate2] = useState('');
  const [payeeName, setPayeeName] = useState('');
  const [achLast4, setAchLast4] = useState('');
  const [achPlaintext, setAchPlaintext] = useState('');
  const [achToken, setAchToken] = useState('');
  const [studentAnswers, setStudentAnswers] = useState<
    FoundationFormAnswerInput[]
  >([]);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const { data: permissions } = useApiGet<string[]>(
    ['my-permissions-foundation-detail'],
    () => getMyPermissions({ international: '1' }),
    { enabled: true }
  );
  const canReview = hasUserApplicationPermission(
    permissions,
    'foundation:review'
  );
  const canManage = hasUserApplicationPermission(
    permissions,
    'foundation:manage'
  );

  const {
    data: app,
    isLoading,
    refetch,
  } = useApiGet<FoundationApplicationDetail>(
    ['foundation-application', id],
    () => getFoundationApplication(id),
    { enabled: !!id, retry: 1 }
  );

  const { data: searchData } = useApiGet<{
    items: FoundationInstitutionSearchResult[];
  }>(
    ['foundation-institution-search', institutionQuery],
    () => searchFoundationInstitutions(institutionQuery),
    { enabled: institutionQuery.trim().length >= 2, retry: 0 }
  );

  useEffect(() => {
    if (!app) return;
    setStudentAnswers(
      toFormAnswerInputs(app.answers_by_form_kind?.student_verification)
    );
  }, [app]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await refetch();
    } catch (e: any) {
      setError(e?.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  if (isLoading || !app) {
    return (
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <Typography>Loading…</Typography>
      </Container>
    );
  }

  const amount = Number(app.awarded_amount || 0);
  const title =
    `${app.student_first_name || ''} ${app.student_last_name || ''}`.trim() ||
    'Application';
  const waiting = app.waiting || [];

  if (app.stage === 'discarded') {
    return (
      <Container maxWidth="md" sx={{ py: 2 }}>
        <PageHeader
          title={title === 'Application' ? 'Discarded ICH' : title}
          leftDom={undefined}
          rightDom={undefined}
          subtitle={undefined}
        />
        <Alert severity="info" sx={{ mb: 2 }}>
          This draft was discarded and is hidden from the default applicants
          list.
        </Alert>
        <Button onClick={() => navigate('/foundation/applicants')}>
          Back to applicants
        </Button>
      </Container>
    );
  }

  const editorMode: RecommendationEditorMode =
    app.stage === 'draft'
      ? 'draft'
      : (canReview || canManage) && staffEditableStage(app.stage)
        ? 'staff'
        : 'readonly';

  const showReview = app.stage !== 'draft';

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <PageHeader
        title={title === 'Application' ? 'Individual Case History Form' : title}
        leftDom={undefined}
        rightDom={undefined}
        subtitle={undefined}
      />
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        sx={{ mb: 2 }}
        useFlexGap
      >
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          label={FOUNDATION_STAGE_LABELS[app.stage] || app.stage}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`${app.academic_year} · ${app.currency || 'USD'} ${amount.toFixed(2)}`}
        />
        {waiting.map((key) => (
          <Chip
            key={key}
            size="small"
            color="warning"
            label={FOUNDATION_WAITING_LABELS[key] || key}
          />
        ))}
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          run(() => uploadFoundationEnrollmentDocument(id, file));
          e.target.value = '';
        }}
      />
      <Grid container spacing={3}>
        <Grid item xs={12} md={showReview ? 7 : 12}>
          <RecommendationEditor
            application={app}
            mode={editorMode}
            onSaved={async () => {
              await refetch();
            }}
            onSubmitted={async () => {
              await refetch();
            }}
          />
        </Grid>
        {showReview && (
          <Grid item xs={12} md={5}>
            <FoundationReviewPanel
              application={app}
              busy={busy}
              comment={comment}
              onCommentChange={setComment}
              studentAnswers={studentAnswers}
              onStudentAnswersChange={setStudentAnswers}
              institutionQuery={institutionQuery}
              onInstitutionQueryChange={setInstitutionQuery}
              searchItems={searchData?.items || []}
              canReview={canReview}
              canManage={canManage}
              installmentCount={installmentCount}
              onInstallmentCountChange={setInstallmentCount}
              date1={date1}
              date2={date2}
              onDate1Change={setDate1}
              onDate2Change={setDate2}
              payeeName={payeeName}
              onPayeeNameChange={setPayeeName}
              achLast4={achLast4}
              achPlaintext={achPlaintext}
              achToken={achToken}
              onAchLast4Change={setAchLast4}
              onAchPlaintextChange={setAchPlaintext}
              onAchTokenChange={setAchToken}
              onUploadClick={() => fileInputRef.current?.click()}
              onComment={() =>
                run(async () => {
                  await postFoundationReviewAction(id, {
                    action_type: 'comment',
                    body: comment,
                  });
                  setComment('');
                })
              }
              onTrusteeApprove={() =>
                run(() =>
                  postFoundationReviewAction(id, {
                    action_type: 'trustee_approve',
                    body: comment,
                  })
                )
              }
              onEoApprove={() =>
                run(() =>
                  postFoundationReviewAction(id, {
                    action_type: 'eo_approve',
                    body: comment,
                  })
                )
              }
              onStudentVerified={() =>
                run(() =>
                  completeFoundationStudentVerification(id, {
                    answers: studentAnswers,
                  })
                )
              }
              onOverrideHold={() =>
                run(() => resolveFoundationTrusteeHold(id, { approve: true }))
              }
              onDenyHold={() =>
                run(() => resolveFoundationTrusteeHold(id, { approve: false }))
              }
              onMatchInstitution={(institutionId) =>
                run(() =>
                  putFoundationInstitution(id, {
                    match_status: 'matched',
                    accredited_institution_id: institutionId,
                  })
                )
              }
              onChangeSchool={() =>
                run(() =>
                  patchFoundationApplication(id, {
                    provisional_institution: {
                      name:
                        app.institution?.matched_name ||
                        app.institution?.provisional_name ||
                        '',
                      accredited_institution_id: '',
                    },
                  })
                )
              }
              onException={() =>
                run(() =>
                  putFoundationInstitution(id, {
                    match_status: 'exception_approved',
                    exception_reason: 'Trustee exception',
                  })
                )
              }
              onSaveAch={() =>
                run(async () => {
                  const res = await saveFoundationACH(id, {
                    last4: achLast4,
                    plaintext: achPlaintext || undefined,
                    token: achToken || undefined,
                  });
                  if (res.token) setAchToken(res.token);
                })
              }
              onFinalApprove={() => {
                const count = Number(installmentCount) || 1;
                const amounts =
                  count === 1
                    ? [amount]
                    : [Math.round((amount / 2) * 100) / 100, 0];
                if (count === 2) {
                  amounts[1] = Math.round((amount - amounts[0]) * 100) / 100;
                }
                const dates = [date1, ...(count > 1 && date2 ? [date2] : [])];
                return run(() =>
                  finalApproveFoundationApplication(id, {
                    installment_count: count,
                    amounts,
                    scheduled_dates: dates,
                    payment_method: achToken ? 'ach' : 'check',
                    payee: {
                      payee_type: 'institution',
                      name: payeeName,
                      ach_lockdown_token: achToken || undefined,
                      ach_last4: achToken ? achLast4 : undefined,
                    },
                  })
                );
              }}
            />
          </Grid>
        )}
      </Grid>
    </Container>
  );
}
